import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { CardTypeConfigDetail } from '../../../../../core/models/card-quota/card-type-config.model';
import { IdNameOption } from '../../../../../core/models/parking/parking-card-list.model';
import { MapperCardTypeConfigService } from '../../../../../core/mappers/card-quota/mapper-card-type-config.service';
import { ApiCardQuotaService } from '../../../../../core/services/api/card-quota/api-card-quota.service';
import { ApiParkingCardListService } from '../../../../../core/services/api/parking/api-parking-card-list.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';

@Component({
  selector: 'app-modal-card-type-config',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './modal-card-type-config.component.html',
  styleUrl: './modal-card-type-config.component.css',
})
export class ModalCardTypeConfigComponent implements OnInit {
  @Input() configId = 0;
  @Input() siteId = 0;
  @Input() siteName = '';
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  private static readonly SUBSCRIPTION_OPTIONS_PREVIEW = 20;

  formData!: CardTypeConfigDetail;
  cardTypes: Array<{ id: number; name: string }> = [];
  subscriptions: IdNameOption[] = [];
  subscriptionSearchText = '';
  isSubscriptionDropdownOpen = false;
  isLoading = signal(false);
  isSaving = signal(false);

  scopeOptions = [
    { value: 'free', label: 'บัตรฟรี (free)' },
    { value: 'expiry', label: 'บัตรมีหมดอายุ (expiry)' },
    { value: 'both', label: 'ทั้งสองประเภท (both)' },
  ];

  private readonly api = inject(ApiCardQuotaService);
  private readonly cardTypeApi = inject(ApiParkingCardListService);
  private readonly mapper = inject(MapperCardTypeConfigService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  readonly siteState = inject(SiteStateService);

  ngOnInit(): void {
    this.formData = this.mapper.createEmptyDetail(this.siteId, this.siteName);
    this.loadCardTypes();
    this.loadSubscriptions();
    if (this.configId > 0) {
      this.loadDetail(this.configId);
    }
  }

  get subscriptionDropdownOptions(): IdNameOption[] {
    const sorted = [...this.subscriptions].sort((a, b) => a.id - b.id);
    const q = this.subscriptionSearchText.trim().toLowerCase();
    if (!q) return sorted.slice(0, ModalCardTypeConfigComponent.SUBSCRIPTION_OPTIONS_PREVIEW);
    return sorted.filter(
      (s) => s.name.toLowerCase().includes(q) || String(s.id).includes(q)
    );
  }

  get selectedSubscriptionLabel(): string {
    if (!this.formData.subscriptionId) return '';
    const sub = this.subscriptions.find((s) => s.id === this.formData.subscriptionId);
    return sub ? `${sub.name} (ID ${sub.id})` : `ID ${this.formData.subscriptionId}`;
  }

  onClose(): void {
    this.close.emit();
  }

  onSave(): void {
    if (!this.siteId) {
      this.toast.error('กรุณาเลือกลานจาก Sidebar');
      return;
    }
    if (!this.formData.cardTypeId) {
      this.toast.error('กรุณาเลือกประเภทบัตร');
      return;
    }
    if (!this.formData.subscriptionId) {
      this.toast.error('กรุณาเลือก Subscription');
      return;
    }

    this.formData.siteId = this.siteId;
    this.formData.siteName = this.siteName || this.siteState.selectedSite()?.name || '';

    const isEdit = this.configId > 0;
    if (isEdit && !this.permissionService.canUpdate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!isEdit && !this.permissionService.canCreate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    const payload = this.mapper.mapToPayload(this.formData, userId, menuId, this.siteId);
    this.isSaving.set(true);
    this.api.saveCardTypeConfig(payload).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapActionResponse(res);
        this.isSaving.set(false);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'บันทึกสำเร็จ');
          this.saveSuccess.emit();
          this.onClose();
        } else {
          this.toast.error(mapped.message || 'บันทึกไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาด');
      },
    });
  }

  openSubscriptionDropdown(): void {
    this.isSubscriptionDropdownOpen = true;
    if (this.formData.subscriptionId && !this.subscriptionSearchText.trim()) {
      this.subscriptionSearchText = this.selectedSubscriptionLabel;
    }
  }

  closeSubscriptionDropdown(): void {
    this.isSubscriptionDropdownOpen = false;
    if (this.formData.subscriptionId) {
      this.subscriptionSearchText = this.selectedSubscriptionLabel;
    } else {
      this.subscriptionSearchText = '';
    }
  }

  onSubscriptionSearchInput(): void {
    this.isSubscriptionDropdownOpen = true;
    if (!this.subscriptionSearchText.trim()) {
      this.formData.subscriptionId = null;
      return;
    }
    const selected = this.subscriptions.find((s) => s.id === this.formData.subscriptionId);
    const label = selected ? `${selected.name} (ID ${selected.id})` : '';
    if (selected && label.toLowerCase() !== this.subscriptionSearchText.trim().toLowerCase()) {
      this.formData.subscriptionId = null;
    }
  }

  selectSubscription(sub: IdNameOption): void {
    this.formData.subscriptionId = sub.id;
    this.formData.subscriptionName = sub.name;
    this.subscriptionSearchText = `${sub.name} (ID ${sub.id})`;
    this.isSubscriptionDropdownOpen = false;
  }

  private loadCardTypes(): void {
    this.cardTypeApi.getParkingCardTypes().subscribe({
      next: (res) => (this.cardTypes = res ?? []),
      error: () => (this.cardTypes = []),
    });
  }

  private loadSubscriptions(): void {
    if (!this.siteId) {
      this.subscriptions = [];
      return;
    }

    this.cardTypeApi.getAllParkingSubscriptionsBySite(this.siteId).subscribe({
      next: (res) => {
        this.subscriptions = (res ?? []).map((item) => ({
          id: Number(item.id ?? 0),
          name: String(item.name ?? ''),
        }));
        if (this.formData.subscriptionId) {
          this.subscriptionSearchText = this.selectedSubscriptionLabel;
        }
      },
      error: () => {
        this.subscriptions = [];
        this.toast.error('โหลด Subscription ล้มเหลว');
      },
    });
  }

  private loadDetail(id: number): void {
    this.isLoading.set(true);
    this.api.getCardTypeConfigById(id).subscribe({
      next: (res) => {
        this.formData = this.mapper.mapDetail(res as CardTypeConfigDetail & Record<string, unknown>);
        this.formData.siteId = this.siteId;
        this.formData.siteName = this.siteName;
        if (this.formData.subscriptionId) {
          this.subscriptionSearchText = this.selectedSubscriptionLabel;
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.toast.error('โหลดรายละเอียดล้มเหลว');
      },
    });
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }
}
